import { useEffect, useState } from 'react';

/**
 * Capability tier for progressive enhancement (inspired by the Shopify Editions
 * approach, scaled for West-African mobile):
 *   0 — static. Reduced-motion, save-data, or weak device/network. Also what the
 *       static prerender (SSR) and AI crawlers see.
 *   1 — minimal motion.
 *   2 — moderate (capable phones/tablets).
 *   3 — full desktop atmosphere.
 *
 * Every flourish gates on this, so a low-end Android in Lomé (or a crawler) never
 * pays for motion it can't afford.
 */
export type Tier = 0 | 1 | 2 | 3;

interface NetworkInfo {
  saveData?: boolean;
  effectiveType?: string;
}

function detectTier(): Tier {
  if (typeof window === 'undefined') return 0; // prerender / SSR → static
  const mql = window.matchMedia;
  if (mql && mql('(prefers-reduced-motion: reduce)').matches) return 0;

  const nav = navigator as Navigator & { deviceMemory?: number; connection?: NetworkInfo };
  const net = nav.connection ?? {};
  if (net.saveData) return 0;

  const mem = nav.deviceMemory ?? 4;
  const cores = nav.hardwareConcurrency ?? 4;
  const et = net.effectiveType ?? '4g';
  const coarse = mql ? mql('(pointer: coarse)').matches : false;

  if (et === 'slow-2g' || et === '2g' || mem <= 2) return 0;
  if (et === '3g' || mem <= 3 || cores <= 4) return 1;
  if (coarse) return 2; // capable touch device
  return 3; // desktop, full atmosphere
}

/**
 * Returns the current tier. Starts at 0 (matches the prerendered HTML), then
 * upgrades once on mount — so first paint is always the fast static version and
 * hydration never causes a layout shift.
 */
export function useTier(): Tier {
  const [tier, setTier] = useState<Tier>(0);
  useEffect(() => {
    setTier(detectTier());
  }, []);
  return tier;
}
